import React, { Fragment, useCallback } from 'react';
import styled from 'styled-components';
import { find } from 'lodash';

import CurveThumbnail from 'components/CurveThumbnail';

import Break from 'components/Break';

import {
  API_FIELD_MAPPINGS,
  OP_MOD_FIXED_VAR_REF_TYPES,
  RESOURCE_TYPES,
  SIDEPANEL_STATES,
  CONTROL_TYPES,
  defaultRowHeight,
  defaultItemsToDisplayInSingleRow,
  defaultSecondaryRowHeight
} from 'utils/appConstants';

import { SubPanel, CurveThumbnailContainer } from 'components/PanelComponents';
import { useDispatch, useSelector } from 'react-redux';
import { setSidePanel } from 'reducers/sidePanel/actions';
import {
  displayFloatingPoint,
  formatCurveDataValues,
  durationFormatter
} from 'utils/format';
import Table, { defaultColumnDefinitions } from './Table';

// The table will only include one result, so don't include these options
const customDefaultColumnDefs = {
  ...defaultColumnDefinitions,
  sortable: false,
  filter: false
};

const curveColumnDefs = [
  {
    headerName: 'Curve',
    field: 'curveDataArray',
    maxWidth: 60,
    cellRenderer: ({ value }) => (
      <CurveThumbnailContainer>
        <CurveThumbnail curveData={value} />
      </CurveThumbnailContainer>
    )
  },
  {
    headerName: 'Curve ID',
    field: 'curveId'
  },
  {
    headerName: 'Curve Type',
    field: 'curveType',
    cellRenderer: ({ value }) => (
      <div>{API_FIELD_MAPPINGS.CURVE.type[value].displayText}</div>
    )
  },
  {
    headerName: 'Curve Points',
    field: 'curveDataArray',
    cellRenderer: row => {
      // Limit the amount of items to display
      const { curveDataArray = [], xMultiplier, yMultiplier } = row.data;
      const curveDataDisplayPoints =
        curveDataArray.length > defaultItemsToDisplayInSingleRow
          ? curveDataArray
              .slice(0, defaultItemsToDisplayInSingleRow)
              .concat(['...'])
          : curveDataArray;
      return (
        <CoordinatesContainer>
          {curveDataDisplayPoints.map((coords, i) => {
            const hasXValue = Object.prototype.hasOwnProperty.call(
              coords,
              'xvalue'
            );
            const hasYValue = Object.prototype.hasOwnProperty.call(
              coords,
              'yvalue'
            );
            if (hasXValue && hasYValue) {
              return (
                <CoordinateCoordinate key={i}>
                  ({formatCurveDataValues(coords.xvalue, xMultiplier)},{' '}
                  {formatCurveDataValues(coords.yvalue, yMultiplier)})
                </CoordinateCoordinate>
              );
            }
            return (
              <CoordinateCoordinate key={i}>{coords}</CoordinateCoordinate>
            );
          })}
        </CoordinatesContainer>
      );
    }
  }
];

const CoordinatesContainer = styled.ul`
  margin: 0 auto;
`;

const CoordinateCoordinate = styled.li`
  padding: 2px;
`;

const ControlTypeContainer = styled.div`
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  border-radius: 2px;
  border: 0.5px solid #e6e6e6;
  background-color: #ffffff;
  margin: 8px 0;
  width: 95%;
  padding: 10px;
`;

const ControlTypeLabel = styled.p`
  font-size: 12px;
`;

const ControlTypeDisplay = styled.div`
  font-size: 12px;
  margin-left: 12px;
`;

const NodeInfoRow = styled.div`
  margin: 10px 0px;
  font-size: 12px;
  display: flex;
`;

const NodeInfoType = styled.div`
  min-width: 150px;
  display: inline-block;
`;

const NodeInfoData = styled.div`
  width: 100%;
`;

const ControlFieldsContainer = ({ controlTypes }) => {
  const dispatch = useDispatch();
  const curves = useSelector(state => state.curve.curves);
  const onCurveSelected = useCallback(
    row => {
      const { curveId } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.CURVE,
          state: SIDEPANEL_STATES.READ,
          id: curveId,
          keepBreadcrumbs: true
        })
      );
    },
    [dispatch]
  );

  const getRowHeight = ({ data }) => {
    const { curveDataArray } = data;

    if (curveDataArray) {
      if (curveDataArray.length > defaultItemsToDisplayInSingleRow) {
        return (
          defaultRowHeight +
          defaultSecondaryRowHeight * defaultItemsToDisplayInSingleRow +
          30
        );
      }
      return (
        defaultRowHeight +
        defaultSecondaryRowHeight * (curveDataArray.length - 1) +
        30
      );
    }
    return defaultRowHeight;
  };

  const renderControlType = (controlTypeName, controlProperty) => {
    switch (controlTypeName) {
      case 'opModConnect':
        if (controlProperty) {
          return <p>Connected</p>;
        } else {
          return <p>Disconnected</p>;
        }

      case 'opModEnergize':
        if (controlProperty) {
          return <p>Energized</p>;
        } else {
          return <p>De-Energized</p>;
        }

      case 'opModFixedPFInjectW':
      case 'opModFixedPFAbsorbW':
        return (
          <Fragment>
            <NodeInfoRow>
              <NodeInfoType>Power Factor</NodeInfoType>
              <NodeInfoData>
                {displayFloatingPoint(
                  controlProperty.displacement,
                  controlProperty.multiplier
                )}
              </NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Displacement</NodeInfoType>
              <NodeInfoData>{controlProperty.displacement}</NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Excitation</NodeInfoType>
              <NodeInfoData>
                {controlProperty.excitation ? 'Under-Excited' : 'Over-Excited'}
              </NodeInfoData>
            </NodeInfoRow>
          </Fragment>
        );

      case 'opModFixedVar':
        return (
          <Fragment>
            <NodeInfoRow>
              <NodeInfoType>Ref Type</NodeInfoType>
              <NodeInfoData>
                {
                  find(OP_MOD_FIXED_VAR_REF_TYPES, {
                    value: controlProperty.refType
                  }).displayText
                }
              </NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Value</NodeInfoType>
              <NodeInfoData>{controlProperty.value / 100}%</NodeInfoData>
            </NodeInfoRow>
          </Fragment>
        );

      case 'opModFixedW':
        const isPositiveRate = controlProperty / 100 >= 0;
        const absoluteValue = Math.abs(controlProperty / 100);
        return (
          <p>
            {isPositiveRate ? 'Discharge' : 'Charge'} Rate: {absoluteValue}%
          </p>
        );

      case 'opModMaxLimW':
        return <p>{controlProperty / 100}%</p>;

      case 'rampTms':
        return <p>{durationFormatter(controlProperty, true)}</p>;

      case 'opModTargetW':
      case 'opModTargetVar':
        return (
          <Fragment>
            <NodeInfoRow>
              <NodeInfoType>Value</NodeInfoType>
              <NodeInfoData>
                {controlProperty.value}
                {CONTROL_TYPES[controlTypeName].unit}
              </NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Multiplier (x10ˣ)</NodeInfoType>
              <NodeInfoData>{controlProperty.multiplier}</NodeInfoData>
            </NodeInfoRow>
          </Fragment>
        );
      case 'opModFreqDroop':
        return (
          <>
            <NodeInfoRow>
              <NodeInfoType>Over-Frequency Dead Band</NodeInfoType>
              <NodeInfoData>{controlProperty.dBOF / 1000}Hz</NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Under-Frequency Dead Band</NodeInfoType>
              <NodeInfoData>{controlProperty.dBUF / 1000}Hz</NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Over-Frequency Frequency Change</NodeInfoType>
              <NodeInfoData>{controlProperty.kOF / 1000}</NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Under-frequency Frequency Change</NodeInfoType>
              <NodeInfoData>{controlProperty.kUF / 1000}</NodeInfoData>
            </NodeInfoRow>
            <NodeInfoRow>
              <NodeInfoType>Open Loop Response Time</NodeInfoType>
              <NodeInfoData>
                {durationFormatter(
                  parseFloat(controlProperty.openLoopTms),
                  true
                )}
              </NodeInfoData>
            </NodeInfoRow>
          </>
        );

      case 'opModFreqWattCurveId':
      case 'opModHFRTMayTripCurveId':
      case 'opModHFRTMustTripCurveId':
      case 'opModHVRTMayTripCurveId':
      case 'opModHVRTMomentaryCessationCurveId':
      case 'opModHVRTMustTripCurveId':
      case 'opModLFRTMayTripCurveId':
      case 'opModLFRTMustTripCurveId':
      case 'opModLVRTMayTripCurveId':
      case 'opModLVRTMomentaryCessationCurveId':
      case 'opModLVRTMustTripCurveId':
      case 'opModVoltVarCurveId':
      case 'opModVoltWattCurveId':
      case 'opModWattPFCurveId':
      case 'opModWattVarCurveId':
        const singleCurveArr = curves.find(
          curve => curve.curveId === controlProperty
        );
        if (singleCurveArr === null || singleCurveArr === undefined) {
          return <p>Curve Removed</p>;
        } else {
          return (
            <Table
              columnDefs={curveColumnDefs}
              rowData={[singleCurveArr]}
              height="200px"
              width="100%"
              padding="0px"
              margin="10px 0px"
              onRowClicked={onCurveSelected}
              pagination={false}
              getRowHeight={getRowHeight}
              defaultColDef={customDefaultColumnDefs}
            />
          );
        }
      default:
        return <p>Error...</p>;
    }
  };
  return (
    <SubPanel>
      {Object.keys(controlTypes).map(controlKey => {
        const controlTypeName = CONTROL_TYPES[controlKey].name;
        return (
          <ControlTypeContainer key={controlKey}>
            <ControlTypeLabel>{controlTypeName}</ControlTypeLabel>
            <Break margin="6px" />
            <ControlTypeDisplay>
              {renderControlType(controlKey, controlTypes[controlKey])}
            </ControlTypeDisplay>
          </ControlTypeContainer>
        );
      })}
    </SubPanel>
  );
};

export default ControlFieldsContainer;
